/**
 * Prints page images and nothing else from the screen (#494).
 *
 * In a hidden frame of our own rather than a new window, which a popup
 * blocker refuses, or `@media print` rules over the app, which would have to
 * know about every screen the viewer is mounted in. A `srcdoc` frame shares
 * the page's origin, so the blob URLs the pages are held as still load in it.
 */

const FRAME_MARK = 'data-print-frame'

function escapeAttribute(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
}

/**
 * The document that is printed: one image per sheet, scaled to fit it.
 *
 * The break goes *before* every page but the first, not after each one, so
 * the last page is not followed by an empty sheet.
 */
export function printMarkup(urls: readonly string[]): string {
  const pages = urls
    .map((url) => `<div class="page"><img src="${escapeAttribute(url)}" alt=""></div>`)
    .join('')
  return [
    '<!doctype html><html><head><meta charset="utf-8"><title>Receita</title><style>',
    '@page{margin:10mm}',
    'html,body{margin:0;padding:0}',
    '.page{break-inside:avoid;page-break-inside:avoid}',
    '.page+.page{break-before:page;page-break-before:always}',
    'img{display:block;margin:0 auto;max-width:100%;max-height:100vh;object-fit:contain}',
    `</style></head><body>${pages}</body></html>`,
  ].join('')
}

/**
 * Opens the browser's print dialog over `urls`, one per sheet.
 *
 * Returns false when there is nothing to print.
 */
export function printPages(urls: readonly string[], host: Document = document): boolean {
  if (urls.length === 0) return false

  // A frame left by a dialog whose `afterprint` never reached us.
  host.querySelector(`iframe[${FRAME_MARK}]`)?.remove()

  const frame = host.createElement('iframe')
  frame.setAttribute(FRAME_MARK, '')
  frame.setAttribute('aria-hidden', 'true')
  frame.tabIndex = -1
  // Off screen, not `display: none`: Firefox and Safari print a frame that
  // has no layout as a blank sheet.
  frame.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'

  // The frame's load waits for its images, so the dialog never opens on a
  // page that has not been drawn yet.
  frame.addEventListener(
    'load',
    () => {
      const target = frame.contentWindow
      if (target === null) return
      target.addEventListener('afterprint', () => frame.remove())
      target.focus()
      target.print()
    },
    { once: true },
  )
  frame.srcdoc = printMarkup(urls)
  host.body.appendChild(frame)
  return true
}
